"use client";

/**
 * PartialResults — progressive results shown while a job is still processing.
 * Port of reactjs/src/pages/analyse/PartialResults.tsx
 */
import type { AnalysisState } from "../types";

interface Props {
  analysisState: AnalysisState;
}

export function PartialResults({ analysisState }: Props) {
  if (!analysisState.partialResults || !analysisState.isProcessing) return null;

  const partial = analysisState.partialResults;

  function getSeverityBg(severity: string): string {
    const map: Record<string, string> = {
      critical: "rgba(239,68,68,0.06)",
      warning: "rgba(251,146,60,0.06)",
      success: "rgba(34,197,94,0.06)",
    };
    return map[severity] ?? "rgba(99,102,241,0.06)";
  }

  function getSeverityBorder(severity: string): string {
    const map: Record<string, string> = {
      critical: "rgba(239,68,68,0.2)",
      warning: "rgba(251,146,60,0.2)",
      success: "rgba(34,197,94,0.2)",
    };
    return map[severity] ?? "rgba(99,102,241,0.2)";
  }

  const insightsRaw = partial.insights as any;
  const insightList: any[] = Array.isArray(insightsRaw)
    ? insightsRaw
    : insightsRaw?.domain_insights || insightsRaw?.insights || [];

  const metricsEntries = partial.metrics
    ? Object.entries(partial.metrics as any).slice(0, 6)
    : [];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }} id="analysis-results-container">
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 600 }}>Analysis Results</h2>
        <div style={{
          display: "inline-flex", alignItems: "center", gap: 6,
          padding: "4px 12px", borderRadius: 20,
          background: "rgba(99,102,241,0.1)", border: "1px solid rgba(99,102,241,0.25)",
          fontSize: 13, color: "var(--indigo)",
        }}>
          <SpinnerIcon />
          Processing — live updates
        </div>
      </div>

      {/* Dataset summary */}
      {partial.dataset_summary && (
        <div style={{
          padding: "16px 20px",
          background: "var(--paper-1)",
          border: "1px solid var(--line-soft)",
          borderLeft: "4px solid rgba(99,102,241,0.6)",
          borderRadius: 10,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, fontSize: 14, fontWeight: 600 }}>
            <FileIcon /> Dataset Overview
          </div>
          <p style={{ margin: 0, fontSize: 14, lineHeight: 1.6 }}>{partial.dataset_summary}</p>
          {partial.primary_domain && (
            <span style={{
              display: "inline-block", marginTop: 10,
              padding: "2px 10px", borderRadius: 20,
              border: "1px solid var(--line-soft)", fontSize: 12,
            }}>
              Domain: {partial.primary_domain}
            </span>
          )}
          {partial.column_mapping && (
            <div style={{ marginTop: 12 }}>
              <p style={{ fontSize: 11, color: "var(--ink-soft)", marginBottom: 4 }}>Column Mapping</p>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {Object.entries((partial.column_mapping as any)?.mapped_columns || {})
                  .slice(0, 9)
                  .map(([col, mapped]: [string, any]) => (
                    <span key={col} style={{
                      padding: "2px 8px", borderRadius: 6,
                      background: "var(--paper-2)", fontSize: 12,
                    }}>
                      <span style={{ opacity: 0.6 }}>{col}</span>
                      {mapped && <span style={{ color: "var(--indigo)", marginLeft: 4 }}>→ {String(mapped)}</span>}
                    </span>
                  ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Metrics */}
      {metricsEntries.length > 0 && (
        <div style={{
          padding: "16px 20px",
          background: "var(--paper-1)",
          border: "1px solid var(--line-soft)",
          borderRadius: 10,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, fontSize: 14, fontWeight: 600 }}>
            <BarIcon /> Key Metrics
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 10 }}>
            {metricsEntries.map(([key, val]: [string, any]) => {
              const displayVal =
                typeof val === "object" && val !== null
                  ? val.formatted ?? val.summary ?? (typeof val.value === "number" ? val.value : `${Object.keys(val).length} items`)
                  : val;
              return (
                <div key={key} style={{
                  padding: "10px 12px",
                  background: "var(--paper-2)",
                  borderRadius: 8,
                }}>
                  <p style={{ margin: "0 0 4px", fontSize: 11, textTransform: "capitalize", color: "var(--ink-soft)" }}>
                    {key.replace(/_/g, " ").replace(/\bpct\b/gi, "%")}
                  </p>
                  <p style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>{String(displayVal ?? "—")}</p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Insights */}
      {insightList.length > 0 && (
        <div style={{
          padding: "16px 20px",
          background: "var(--paper-1)",
          border: "1px solid var(--line-soft)",
          borderRadius: 10,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, fontSize: 14, fontWeight: 600 }}>
            <LightbulbIcon /> Strategic Insights
            <span style={{ marginLeft: 4, fontSize: 12, color: "var(--ink-soft)", fontWeight: 400 }}>
              {insightList.length} generated
            </span>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {insightList.slice(0, 5).map((ins: any, idx: number) => (
              <div key={idx} style={{
                padding: "10px 14px",
                borderRadius: 8,
                background: getSeverityBg(ins.severity),
                border: `1px solid ${getSeverityBorder(ins.severity)}`,
              }}>
                <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                  <span style={{
                    padding: "1px 8px", borderRadius: 20, fontSize: 11,
                    border: "1px solid currentColor", textTransform: "capitalize",
                    flexShrink: 0,
                  }}>
                    {ins.severity || "info"}
                  </span>
                  <div>
                    <p style={{ margin: 0, fontSize: 13, fontWeight: 500 }}>{ins.insight}</p>
                    {ins.recommendation && (
                      <p style={{ margin: "4px 0 0", fontSize: 12, opacity: 0.7 }}>
                        💡 {ins.recommendation}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
            {insightList.length > 5 && (
              <p style={{ textAlign: "center", fontSize: 12, color: "var(--ink-soft)" }}>
                +{insightList.length - 5} more insights loading...
              </p>
            )}
          </div>
        </div>
      )}

      {/* Loading skeleton */}
      <div style={{
        padding: "18px 20px",
        background: "var(--paper-1)",
        border: "1px dashed var(--line-soft)",
        borderRadius: 10,
        display: "flex", alignItems: "center", gap: 12,
      }}>
        <SpinnerIcon />
        <div style={{ flex: 1 }}>
          <p style={{ margin: "0 0 2px", fontSize: 13, fontWeight: 500 }}>Loading next section...</p>
          <p style={{ margin: 0, fontSize: 12, color: "var(--ink-soft)" }}>{analysisState.progressMessage}</p>
        </div>
        <span style={{ fontSize: 13, fontWeight: 500, color: "var(--ink-soft)" }}>{analysisState.progress}%</span>
      </div>
    </div>
  );
}

// ─── Inline icons ─────────────────────────────────────────────────────────────

function SpinnerIcon() {
  return (
    <svg
      width="16" height="16" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      style={{ animation: "spin 1s linear infinite", flexShrink: 0 }}
      aria-hidden="true"
    >
      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
    </svg>
  );
}

function FileIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
      <polyline points="14 2 14 8 20 8"/>
    </svg>
  );
}

function BarIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/>
    </svg>
  );
}

function LightbulbIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <line x1="9" y1="18" x2="15" y2="18"/><line x1="10" y1="22" x2="14" y2="22"/>
      <path d="M15.09 14c.18-.98.65-1.74 1.41-2.5A4.65 4.65 0 0 0 18 8 6 6 0 0 0 6 8c0 1 .23 2.23 1.5 3.5A4.61 4.61 0 0 1 8.91 14"/>
    </svg>
  );
}
